import * as React from "react"
import { useState, useEffect, useCallback, useMemo } from "react"
import {
    Box, Grid, Card, Typography, Button, Chip, TextField, InputAdornment,
    ToggleButton, ToggleButtonGroup, Table, TableHead, TableRow, TableCell, TableBody,
    CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, Checkbox
} from "@mui/material"
import { AutocompleteAsyncSeeker, IconSeeker } from "@ui-seeker/core"
import { useAppToast } from "@ui-seeker/app"
import { getSubmit, postSubmit } from "@ui-seeker/utils"

import { BACKEND_APIS } from "@constants"

const CB = BACKEND_APIS.COMPETITOR_BRANDS
const idOf = (v) => (v && typeof v === 'object' ? v.value : v)
const ALL = '__all__'                         // "all sites" sentinel for the site selector
const rowKey = (r) => `${r.id_site ?? ''}|${r.brand}`

/**
 * Competitor-brand normalization for a customer.
 * - Per-site mode (a site selected): review/normalize one competitor site.
 * - All-sites mode (site = "All sites"): a brand across EVERY competitor site at
 *   once (brand search required), one row per site, with multi-select + bulk assign.
 * Shared customer selector via the `cid` prop.
 */
export default function CompetitorBrandsPanel({ cid }) {
    const { showToast } = useAppToast()

    const [sites, setSites] = useState([])
    const [siteId, setSiteId] = useState('')
    const [rows, setRows] = useState([])
    const [loading, setLoading] = useState(false)
    const [running, setRunning] = useState(false)
    const [search, setSearch] = useState('')
    const [status, setStatus] = useState('all')
    const [endDate, setEndDate] = useState('')
    const [firstDate, setFirstDate] = useState('')   // products NEW since (first_date); empty = off
    const [assignTarget, setAssignTarget] = useState(null)   // {brand,id_site} | {bulk:true,targets:[...]}
    const [assignBrand, setAssignBrand] = useState(null)
    const [savingAssign, setSavingAssign] = useState(false)
    const [selected, setSelected] = useState({})             // rowKey -> row (all-sites multi-select)

    const allMode = siteId === ALL

    useEffect(() => {
        setSites([]); setSiteId(''); setRows([])
        if (!cid) return
        getSubmit(`${BACKEND_APIS.BATCH_ADMIN}/competitor-sites?customerID=${cid}`)
            .then(res => setSites(Array.isArray(res) ? res : []))
            .catch(() => { showToast.error('Could not load competitor sites'); setSites([]) })
    }, [cid])

    const load = useCallback(() => {
        setSelected({})
        if (!siteId) { setRows([]); return }
        if (allMode && !search.trim()) { setRows([]); return }   // brand required in all-sites mode
        setLoading(true)
        const params = new URLSearchParams()
        if (status !== 'all') params.set('status', status)
        if (endDate) params.set('endDate', endDate)
        if (firstDate) params.set('firstDate', firstDate)
        let url
        if (allMode) {
            params.set('customerID', cid); params.set('search', search.trim())
            url = `${CB}/across-sites?${params.toString()}`
        } else {
            params.set('siteID', siteId)
            if (search.trim()) params.set('search', search.trim())
            url = `${CB}?${params.toString()}`
        }
        getSubmit(url)
            .then(res => setRows(res?.brands || []))
            .catch(() => { showToast.error('Could not load competitor brands'); setRows([]) })
            .finally(() => setLoading(false))
    }, [siteId, allMode, cid, search, status, endDate, firstDate])

    useEffect(() => {
        const t = setTimeout(load, 300)
        return () => clearTimeout(t)
    }, [load])

    const siteName = useCallback((id) => sites.find(s => idOf(s) === id)?.label || `Site ${id}`, [sites])

    const counts = useMemo(() => ({
        total: rows.length,
        normalized: rows.filter(r => r.normalized).length,
        pending: rows.filter(r => !r.normalized).length,
    }), [rows])

    const selectedRows = useMemo(() => Object.values(selected), [selected])
    const toggleRow = (r) => setSelected(prev => {
        const k = rowKey(r); const next = { ...prev }
        if (next[k]) delete next[k]; else next[k] = r
        return next
    })
    const allChecked = rows.length > 0 && rows.every(r => selected[rowKey(r)])
    const toggleAll = () => setSelected(allChecked ? {} : Object.fromEntries(rows.map(r => [rowKey(r), r])))

    const runNormalize = async () => {
        if (!siteId || allMode) return
        setRunning(true)
        try {
            const res = await postSubmit(`${CB}/normalize`, { id_site: siteId })
            showToast.success(`Normalized: ${res.from_sku_products || 0} products from their SKU · ${res.matched_texts} brands / ${res.matched_products} products by keywords · ambiguous ${res.ambiguous_texts} · unmatched ${res.unmatched_texts}`)
            load()
        } catch (e) {
            showToast.error('Error normalizing competitor brands')
        } finally {
            setRunning(false)
        }
    }

    const doAssign = async (id_brand) => {
        setSavingAssign(true)
        try {
            if (assignTarget?.bulk) {
                const targets = assignTarget.targets.map(r => ({ id_site: r.id_site, brand: r.brand }))
                const res = await postSubmit(`${CB}/assign-bulk`, { id_brand, targets })
                showToast.success(`${res.updated} products updated across ${targets.length} rows`
                    + (res.failed ? ` · ${res.failed} failed` : ''))
            } else {
                await postSubmit(`${CB}/assign`, {
                    id_site: assignTarget.id_site ?? siteId, brand: assignTarget.brand, id_brand
                })
                showToast.success(id_brand ? 'Brand normalized' : 'Normalization cleared')
            }
            setAssignTarget(null); setAssignBrand(null); setSelected({})
            load()
        } catch (e) {
            showToast.error('Error assigning brand')
        } finally {
            setSavingAssign(false)
        }
    }

    if (!cid) {
        return (
            <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
                <IconSeeker icon="tags" sx={{ fontSize: 48, mb: 1 }} />
                <Typography>Select a customer first.</Typography>
            </Box>
        )
    }

    const needsSearch = allMode && !search.trim()

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <TextField select size="small" label="Competitor site" sx={{ minWidth: 220 }}
                               value={siteId} onChange={e => setSiteId(e.target.value)}
                               disabled={sites.length === 0}>
                        <MenuItem value={ALL}>🌐 All sites (brand search)</MenuItem>
                        {sites.map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
                    </TextField>
                    <TextField size="small" placeholder={allMode ? "Search brand (required)…" : "Search brand…"}
                               value={search} onChange={e => setSearch(e.target.value)} sx={{ minWidth: 220 }}
                               error={needsSearch}
                               InputProps={{ startAdornment: (
                                   <InputAdornment position="start"><IconSeeker icon="search" sx={{ color: '#9ca3af' }} /></InputAdornment>
                               ) }} />
                    <ToggleButtonGroup size="small" exclusive value={status}
                                       onChange={(e, v) => v && setStatus(v)}>
                        <ToggleButton value="all">All</ToggleButton>
                        <ToggleButton value="pending">Pending</ToggleButton>
                        <ToggleButton value="normalized">Normalized</ToggleButton>
                    </ToggleButtonGroup>
                    <TextField size="small" type="date" label="Seen since (end date)"
                               value={endDate} onChange={e => setEndDate(e.target.value)}
                               InputLabelProps={{ shrink: true }} sx={{ minWidth: 185 }} />
                    <TextField size="small" type="date" label="New since (first date)"
                               value={firstDate} onChange={e => setFirstDate(e.target.value)}
                               InputLabelProps={{ shrink: true }} sx={{ minWidth: 185 }}
                               inputProps={{ title: 'Only count products whose SKU_MAPPING first_date is on/after this date (new since)' }} />
                    <Box sx={{ flex: 1 }} />
                    {siteId && !allMode && (
                        <Typography variant="body2" color="text.secondary">
                            {counts.total} brands · {counts.normalized} normalized · {counts.pending} pending
                        </Typography>
                    )}
                    {!allMode && (
                        <Button variant="contained" disabled={!siteId || running}
                                startIcon={running ? <CircularProgress size={16} /> : <IconSeeker icon="wand-magic-sparkles" />}
                                onClick={runNormalize}>
                            Normalize (keywords)
                        </Button>
                    )}
                </Box>
            </Grid>

            {/* Bulk-assign bar (all-sites multi-select) */}
            {allMode && selectedRows.length > 0 && (
                <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1.5, borderRadius: 1,
                               bgcolor: 'action.selected' }}>
                        <Typography variant="body2"><strong>{selectedRows.length}</strong> rows selected
                            {' '}({selectedRows.reduce((s, r) => s + (r.product_count || 0), 0).toLocaleString()} products)</Typography>
                        <Box sx={{ flex: 1 }} />
                        <Button size="small" onClick={() => setSelected({})}>Clear</Button>
                        <Button size="small" variant="contained" startIcon={<IconSeeker icon="tags" />}
                                onClick={() => { setAssignTarget({ bulk: true, targets: selectedRows }); setAssignBrand(null) }}>
                            Assign canonical brand to selected
                        </Button>
                    </Box>
                </Grid>
            )}

            <Grid item xs={12}>
                {!siteId ? (
                    <Box sx={{ textAlign: 'center', py: 5, color: 'text.secondary' }}>
                        <Typography>Select a competitor site — or "All sites" and search a brand.</Typography>
                    </Box>
                ) : needsSearch ? (
                    <Box sx={{ textAlign: 'center', py: 5, color: 'text.secondary' }}>
                        <Typography>Type a brand to see it across all competitor sites.</Typography>
                    </Box>
                ) : (
                    <Card variant="outlined">
                        {loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress /></Box>
                        ) : rows.length === 0 ? (
                            <Box sx={{ textAlign: 'center', py: 5, color: 'text.secondary' }}>No brands found.</Box>
                        ) : (
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        {allMode && (
                                            <TableCell padding="checkbox">
                                                <Checkbox size="small" checked={allChecked}
                                                          indeterminate={selectedRows.length > 0 && !allChecked}
                                                          onChange={toggleAll} />
                                            </TableCell>
                                        )}
                                        {allMode && <TableCell>Site</TableCell>}
                                        <TableCell>Competitor brand</TableCell>
                                        <TableCell align="right">Products</TableCell>
                                        <TableCell>Canonical brand</TableCell>
                                        <TableCell align="right">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rows.map(r => (
                                        <TableRow key={rowKey(r)} hover selected={!!selected[rowKey(r)]}>
                                            {allMode && (
                                                <TableCell padding="checkbox">
                                                    <Checkbox size="small" checked={!!selected[rowKey(r)]}
                                                              onChange={() => toggleRow(r)} />
                                                </TableCell>
                                            )}
                                            {allMode && <TableCell>{siteName(r.id_site)}</TableCell>}
                                            <TableCell sx={{ fontWeight: 500 }}>{r.brand}</TableCell>
                                            <TableCell align="right">{r.product_count.toLocaleString()}</TableCell>
                                            <TableCell>
                                                {r.normalized ? (
                                                    <Chip size="small" color={r.inconsistent ? 'warning' : 'success'} variant="outlined"
                                                          label={r.inconsistent ? `${r.canonical_name || r.id_brand} (mixed)` : (r.canonical_name || r.id_brand)} />
                                                ) : (
                                                    <Chip size="small" variant="outlined" label="— pending" sx={{ color: 'text.disabled' }} />
                                                )}
                                            </TableCell>
                                            <TableCell align="right">
                                                <Button size="small" onClick={() => { setAssignTarget({ brand: r.brand, id_site: r.id_site }); setAssignBrand(null) }}>
                                                    {r.normalized ? 'Reassign' : 'Assign'}
                                                </Button>
                                                {r.normalized && (
                                                    <Button size="small" color="error" onClick={() => { setAssignTarget({ brand: r.brand, id_site: r.id_site }); doAssign(null) }}>
                                                        Clear
                                                    </Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </Card>
                )}
            </Grid>

            <Dialog open={!!assignTarget} onClose={() => setAssignTarget(null)} fullWidth maxWidth="sm">
                <DialogTitle>{assignTarget?.bulk ? 'Normalize selected competitor brands' : 'Normalize competitor brand'}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {assignTarget?.bulk
                            ? <>Applying to <strong>{assignTarget.targets.length}</strong> selected (site, brand) rows.</>
                            : <>Competitor brand: <strong>{assignTarget?.brand}</strong></>}
                    </Typography>
                    <AutocompleteAsyncSeeker
                        urlAjax={`${BACKEND_APIS.BRANDS}/crud/brand/find-options`}
                        label="Canonical brand (DIPI_BRAND_NEW)"
                        placeholder="Search canonical brand…"
                        value={assignBrand}
                        onChange={e => setAssignBrand(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAssignTarget(null)}>Cancel</Button>
                    <Button variant="contained" disabled={!idOf(assignBrand) || savingAssign}
                            startIcon={savingAssign ? <CircularProgress size={16} /> : null}
                            onClick={() => doAssign(idOf(assignBrand))}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </Grid>
    )
}
